import { useState, useEffect } from "react";
import {
  type NewsSlide,
  DEFAULT_SLIDES,
  DEFAULT_AUTO_ROTATE_MS,
  NEWS_JSON_URL,
  ICON_MAP,
} from "../const/homeNewsData";

const CACHE_KEY = "fluxi_home_news";
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutos

interface RemoteSlide {
  id: number;
  imageUrl?: string;
  title?: string;
  bullets?: string[];
  ctaText?: string;
  ctaUrl?: string;
  decorativeIcon?: string;
  listIcon: string;
  listTitle: string;
}

interface RemoteNewsData {
  slides: RemoteSlide[];
  autoRotateMs?: number;
}

function parseRemoteData(data: RemoteNewsData): {
  slides: NewsSlide[];
  autoRotateMs: number;
} {
  const slides: NewsSlide[] = data.slides.map((s) => ({
    id: s.id,
    imageUrl: s.imageUrl,
    title: s.title,
    bullets: s.bullets,
    ctaText: s.ctaText,
    ctaUrl: s.ctaUrl,
    decorativeIcon: s.decorativeIcon
      ? ICON_MAP[s.decorativeIcon]
      : undefined,
    listIcon: ICON_MAP[s.listIcon] ?? ICON_MAP.Coins,
    listTitle: s.listTitle,
  }));

  return {
    slides,
    autoRotateMs: data.autoRotateMs ?? DEFAULT_AUTO_ROTATE_MS,
  };
}

function getCached(): RemoteNewsData | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw) as { data: RemoteNewsData; ts: number };
    if (Date.now() - entry.ts > CACHE_TTL_MS) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
}

function setCache(data: RemoteNewsData) {
  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ data, ts: Date.now() })
    );
  } catch {
    // sessionStorage lleno, no pasa nada
  }
}

export function useHomeNews() {
  const [slides, setSlides] = useState<NewsSlide[]>(DEFAULT_SLIDES);
  const [autoRotateMs, setAutoRotateMs] = useState(DEFAULT_AUTO_ROTATE_MS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchNews() {
      // Intentar cache primero
      const cached = getCached();
      if (cached) {
        const parsed = parseRemoteData(cached);
        if (!cancelled) {
          setSlides(parsed.slides);
          setAutoRotateMs(parsed.autoRotateMs);
          setLoading(false);
        }
        return;
      }

      try {
        const res = await fetch(NEWS_JSON_URL, { cache: "no-cache" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data: RemoteNewsData = await res.json();

        if (cancelled) return;

        const parsed = parseRemoteData(data);
        setSlides(parsed.slides);
        setAutoRotateMs(parsed.autoRotateMs);
        setCache(data);
      } catch {
        // Fetch fallo — usar datos por defecto (ya estan seteados)
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchNews();
    return () => {
      cancelled = true;
    };
  }, []);

  return { slides, autoRotateMs, loading };
}
